/**
 * Agent Directory default theme — v1 token *values* lifted into the platform's
 * `--brand-*` token model (spec §4: carry v1's values, NOT its hard-coded
 * palette or its Lucide/Untitled-UI icon mix). This is the single source of
 * truth for AD's defaults: every org starts on the v1 look and restyles from
 * here, never relayouts (spec §5 — colors/fonts/logos are per-org; layout,
 * spacing, radius and nav patterns are fixed platform-wide).
 *
 * Source of the carried values: `AgentDirectory-Replit/src/app/globals.css`
 * (teal/teal-dark/teal-light/sky/sun/sand palette + `--radius: 0.625rem`).
 *
 * The matching *CSS* defaults live in `src/app/globals.css` `:root` (the derived
 * `color-mix` tokens are defined there once and recompute per-org). CSS cannot
 * import this TS module, so the two are kept in sync by hand — this file is the
 * authority; globals.css mirrors it.
 */

import type { BrandThemeConfig } from "@to/ui/theme";
import type { AdFooterLink } from "@to/db";

/**
 * v1 (`AgentDirectory-Replit`) palette — the ONLY place AD carries v1's raw hex
 * values. Everything else flows through `--brand-*`; these never appear in
 * component markup (the 29.5 grep enforces that).
 */
export const AD_V1_PALETTE = {
  teal: "#0e8c8c", // primary brand
  tealDark: "#0a6e6e", // → secondary (darker brand shade)
  tealLight: "#12a5a5",
  sky: "#38bdf8",
  sun: "#f5a623", // accent
  sand: "#fdf6ec", // surface / section background
} as const;

/**
 * v1 carry-over corner radius (spec §4). The tested source-of-truth value and a
 * mirror of globals.css `--radius` (which the shell consumes via Tailwind's
 * `rounded-lg` → `var(--radius)`). `buildBrandCSS` does not emit radius.
 */
export const AD_RADIUS = "0.625rem";

/** Neutral body text on the white background (not a v1 brand hue). */
const AD_TEXT = "#1A1A1A";

/** Platform-default product name when an org has not set `product_name`. */
export const AD_DEFAULT_PRODUCT_NAME = "Agent Directory";

/**
 * Initial editable home hero copy in both admin forms. Null means never set;
 * saved empty strings remain blank. The public hero never substitutes defaults.
 * The initial headline is the org name; AD_DEFAULT_HERO_HEADLINE is historical.
 */
export const AD_DEFAULT_HERO_HEADLINE = "Find a Trusted Only REALTOR®";
export const AD_DEFAULT_HERO_SUBHEAD =
  "Search board-verified agents, browse open houses, and connect with trusted local home services.";

/**
 * Launch-market fallback coordinates for the OPTIONAL home weather chip (Bonita
 * Springs / SW Florida). AD has no per-org coordinate source yet (no lat/lng on
 * `ad_org_config` / `synced_locations`); this is the documented fallback AND a
 * forward seam — when P40 admin adds per-org coords, the page resolves
 * `config?.x ?? AD_DEFAULT_MARKET_*` and passes them to `<WeatherChip lat lng>`.
 * Lives here so every AD platform default has one home (next to product name /
 * footer copy / hero copy), not buried in a leaf component.
 */
export const AD_DEFAULT_MARKET_LAT = 26.3398;
export const AD_DEFAULT_MARKET_LON = -81.7787;

/** Platform-default footer copyright when an org has not set `footer_copy`. */
export const AD_DEFAULT_FOOTER_COPY =
  "© Trusted Only LLC. All rights reserved.";

/**
 * Agent Directory default theme as a `@to/ui` `BrandThemeConfig`. Maps the v1
 * palette onto the shared brand-token slots: teal → primary, teal-dark →
 * secondary, sun → accent, sand → sectionBg. `fontPairing: 'geist'` resolves via
 * the shared `FONT_PAIRINGS` registry to the Geist Sans family bound in
 * `layout.tsx` (`--font-geist-sans`). `buildBrandCSS` derives the surface /
 * border / muted tokens from these via `color-mix`.
 */
export const AD_BRAND_DEFAULTS: BrandThemeConfig = {
  primaryColor: AD_V1_PALETTE.teal,
  secondaryColor: AD_V1_PALETTE.tealDark,
  accentColor: AD_V1_PALETTE.sun,
  backgroundColor: "#FFFFFF",
  textColor: AD_TEXT,
  sectionBgColor: AD_V1_PALETTE.sand,
  fontPairing: "geist",
};

/**
 * Row-shaped default values for seeding/inserting an `ad_org_config`. Phase 30's
 * backfill and Phase 40's admin both call this so the defaults have one source
 * of truth. Brand colors carry the full v1 palette into the per-org columns;
 * fonts default to the Geist Sans CSS var (heading + body); product name + footer
 * copy are the platform defaults above. `footerLinks` seeds empty (orgs add their
 * own; the shell falls back to the primary nav for display when empty).
 */
export interface AdOrgConfigDefaults {
  brandPrimaryColor: string;
  brandAccentColor: string;
  brandTealColor: string;
  brandSkyColor: string;
  brandSunColor: string;
  brandSandColor: string;
  headingFont: string;
  bodyFont: string;
  productName: string;
  footerCopy: string;
  footerLinks: AdFooterLink[];
}

export function getAdOrgConfigDefaults(): AdOrgConfigDefaults {
  return {
    brandPrimaryColor: AD_V1_PALETTE.teal,
    brandAccentColor: AD_V1_PALETTE.sun,
    brandTealColor: AD_V1_PALETTE.teal,
    brandSkyColor: AD_V1_PALETTE.sky,
    brandSunColor: AD_V1_PALETTE.sun,
    brandSandColor: AD_V1_PALETTE.sand,
    headingFont: "var(--font-geist-sans)",
    bodyFont: "var(--font-geist-sans)",
    productName: AD_DEFAULT_PRODUCT_NAME,
    footerCopy: AD_DEFAULT_FOOTER_COPY,
    footerLinks: [],
  };
}
